import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Plus, Edit, Trash2, ClipboardList, Clock, X } from 'lucide-react';

const API = `${API_BASE_URL}/api`;

// Format "YYYY-MM-DDTHH:MM" → "YYYY-MM-DD 24-hour HH:MM" for display
const fmt = (dt) => {
  if (!dt) return '—';
  const [d, t] = dt.split('T');
  return `${d || ''}${t ? ` ${t}` : ''}`.trim();
};

const emptyEntry = () => ({ id: null, title: '', fromDateTime: '', toDateTime: '', plan: '', phase: '' });

const PHASE_OPTIONS = ['Prevention', 'Mitigation', 'Preparedness', 'Response', 'Recovery'];
// Solid ICS top-bar colors — match the hex equivalents of the Tailwind classes
// used on the header (bg-green-500 / bg-amber-500 / bg-orange-500 / bg-red-900).
const PHASE_HEX = {
  Prevention:   { bg: '#22c55e', fg: '#052e16' }, // green-500
  Mitigation:   { bg: '#f59e0b', fg: '#1f1300' }, // amber-500
  Preparedness: { bg: '#f97316', fg: '#1a0a00' }, // orange-500
  Response:     { bg: '#7f1d1d', fg: '#fef2f2' }, // red-900
  Recovery:     { bg: '#f59e0b', fg: '#1f1300' }, // amber-500 (same as Mitigation on top bar)
};
const phaseBadgeClass = (phase) => {
  switch (phase) {
    case 'Prevention':    return 'bg-green-500/20 text-green-300 border-green-500/40';
    case 'Mitigation':    return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    case 'Preparedness':  return 'bg-orange-500/20 text-orange-300 border-orange-500/40';
    case 'Response':      return 'bg-red-900/40 text-red-200 border-red-800/60';
    case 'Recovery':      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40';
    default:              return 'bg-gray-700/60 text-gray-200 border-gray-600';
  }
};

/**
 * Reusable "Plan Entries" card. Renders a Title / From / To / Plan form + list.
 * Persists to /api/plan-entries with the given form_type + exercise_id.
 */
const PlanEntriesCard = ({
  formType,
  exerciseId = '',
  title = 'Plan Entries',
  description = 'Time-bounded plan entries — each with a Title, From/To 24-hour date-times, and a free-text Plan',
  cardClass = 'bg-gray-900 border-gray-700',
  currentPhase = '', // If provided, adds a Phase dropdown; new entries prefill with this value
  showToDateTime = true, // If false, hides the "To" field and renders Title + Date/Time on the same row
  dateTimeLabel = 'From', // Label for the (single) date/time field when showToDateTime is false
}) => {
  const [entries, setEntries] = useState([]);
  const [current, setCurrent] = useState(() => ({ ...emptyEntry(), phase: currentPhase || '' }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Keep the "add" form's phase prefilled with the active phase whenever it changes
  // (only while not editing an existing entry — edits preserve the original phase)
  useEffect(() => {
    if (!current.id && currentPhase && current.phase !== currentPhase) {
      setCurrent((p) => ({ ...p, phase: currentPhase }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhase]);

  const authHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    if (!formType) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/plan-entries`, {
        params: { form_type: formType, exercise_id: exerciseId || '' },
        headers: authHeaders(),
      });
      setEntries(res.data || []);
    } catch (e) {
      console.error('load plan entries failed', e);
    } finally {
      setLoading(false);
    }
  }, [formType, exerciseId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!current.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (current.id) {
        const res = await axios.put(
          `${API}/plan-entries/${current.id}`,
          { title: current.title, fromDateTime: current.fromDateTime, toDateTime: current.toDateTime, plan: current.plan, phase: current.phase || '' },
          { headers: authHeaders() }
        );
        setEntries((prev) => prev.map((e) => (e.id === current.id ? res.data : e)));
        toast.success('Plan entry updated');
      } else {
        const res = await axios.post(
          `${API}/plan-entries`,
          {
            form_type: formType,
            exercise_id: exerciseId || '',
            title: current.title,
            fromDateTime: current.fromDateTime,
            toDateTime: current.toDateTime,
            plan: current.plan,
            phase: current.phase || currentPhase || '',
          },
          { headers: authHeaders() }
        );
        setEntries((prev) => [...prev, res.data]);
        toast.success('Plan entry added');
      }
      setCurrent({ ...emptyEntry(), phase: currentPhase || '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete plan entry "${entry.title || 'Untitled'}"?`)) return;
    try {
      await axios.delete(`${API}/plan-entries/${entry.id}`, { headers: authHeaders() });
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      if (current.id === entry.id) setCurrent({ ...emptyEntry(), phase: currentPhase || '' });
      toast.success('Plan entry deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Split/joined date+time helpers so the UI uses two inputs but stores "YYYY-MM-DDTHH:MM"
  const updateDate = (key, dateStr) => {
    const cur = current[key] || '';
    const time = cur.split('T')[1] || '';
    setCurrent((prev) => ({ ...prev, [key]: dateStr ? `${dateStr}T${time}` : '' }));
  };
  const updateTime = (key, timeStr) => {
    let value = timeStr.replace(/[^0-9:]/g, '');
    if (value.length === 2 && !value.includes(':')) value = value + ':';
    if (value.length > 5) return;
    const cur = current[key] || '';
    const date = cur.split('T')[0] || '';
    setCurrent((prev) => ({ ...prev, [key]: date ? `${date}T${value}` : value }));
  };

  return (
    <Card className={cardClass} data-testid={`plan-entries-${formType}`}>
      <CardHeader>
        <CardTitle className="text-orange-500 flex items-center gap-2">
          <ClipboardList className="h-5 w-5" /> {title} ({entries.length})
        </CardTitle>
        {description && <CardDescription className="text-gray-400">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add / Edit form */}
        <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 space-y-3">
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wide">
            {current.id ? 'Edit plan entry' : 'Add plan entry'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={showToDateTime ? 'md:col-span-3' : 'md:col-span-2'}>
              <label className="text-white text-xs font-medium mb-1 block">Title *</label>
              <Input
                data-testid={`pe-${formType}-title`}
                value={current.title}
                onChange={(e) => setCurrent((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Communications Plan — OP-1"
                className="bg-gray-800 border-gray-600 text-white h-10 text-sm"
              />
            </div>
            <div>
              <label className="text-white text-xs font-medium mb-1 block">{dateTimeLabel}</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={(current.fromDateTime || '').split('T')[0] || ''}
                  onChange={(e) => updateDate('fromDateTime', e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white h-10 text-sm"
                  data-testid={`pe-${formType}-from-date`}
                />
                <Input
                  type="text"
                  placeholder="HH:MM"
                  maxLength={5}
                  value={(current.fromDateTime || '').split('T')[1] || ''}
                  onChange={(e) => updateTime('fromDateTime', e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white h-10 text-sm"
                  data-testid={`pe-${formType}-from-time`}
                />
              </div>
            </div>
            {showToDateTime && (
            <div>
              <label className="text-white text-xs font-medium mb-1 block">To</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={(current.toDateTime || '').split('T')[0] || ''}
                  onChange={(e) => updateDate('toDateTime', e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white h-10 text-sm"
                  data-testid={`pe-${formType}-to-date`}
                />
                <Input
                  type="text"
                  placeholder="HH:MM"
                  maxLength={5}
                  value={(current.toDateTime || '').split('T')[1] || ''}
                  onChange={(e) => updateTime('toDateTime', e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white h-10 text-sm"
                  data-testid={`pe-${formType}-to-time`}
                />
              </div>
            </div>
            )}
            <div className="md:col-span-3">
              <label className="text-white text-xs font-medium mb-1 block">Plan</label>
              <Textarea
                value={current.plan}
                onChange={(e) => setCurrent((p) => ({ ...p, plan: e.target.value }))}
                placeholder="Describe the plan, scope, intended actions, responsible parties, special instructions…"
                rows={4}
                className="bg-gray-800 border-gray-600 text-white text-sm resize-y"
                data-testid={`pe-${formType}-plan`}
              />
            </div>
            {currentPhase !== null && (
              <div className="md:col-span-3">
                <label className="text-white text-xs font-medium mb-1 block">Phase</label>
                <select
                  value={current.phase || ''}
                  onChange={(e) => setCurrent((p) => ({ ...p, phase: e.target.value }))}
                  className="border border-gray-600 h-10 text-sm font-semibold rounded-md px-3 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  style={
                    current.phase && PHASE_HEX[current.phase]
                      ? { backgroundColor: PHASE_HEX[current.phase].bg, color: PHASE_HEX[current.phase].fg }
                      : { backgroundColor: '#1f2937', color: '#ffffff' }
                  }
                  data-testid={`pe-${formType}-phase`}
                >
                  <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>— Select phase —</option>
                  {PHASE_OPTIONS.map((p) => (
                    <option
                      key={p}
                      value={p}
                      style={{ backgroundColor: PHASE_HEX[p].bg, color: PHASE_HEX[p].fg, fontWeight: 600 }}
                    >
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold min-h-[40px]"
              data-testid={`pe-${formType}-save-btn`}
            >
              <Plus className="h-4 w-4 mr-1" />
              {current.id ? 'Update entry' : 'Add entry'}
            </Button>
            {current.id && (
              <Button
                onClick={() => setCurrent(emptyEntry())}
                variant="outline"
                size="sm"
                className="border-gray-500 text-gray-300 min-h-[40px]"
                data-testid={`pe-${formType}-cancel-btn`}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-6 text-gray-500 text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">No plan entries added yet</div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`pe-${formType}-row-${entry.id}`}
                className="bg-gray-800 rounded-lg p-3 border border-gray-700"
              >
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-2">
                      <h4 className="text-white font-semibold text-sm">
                        {entry.title || 'Untitled'}
                      </h4>
                      {entry.phase && (
                        <span
                          data-testid={`pe-${formType}-phase-badge-${entry.id}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${phaseBadgeClass(entry.phase)}`}
                        >
                          Phase: {entry.phase}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {showToDateTime ? 'From:' : `${dateTimeLabel}:`}&nbsp;
                        <span className="text-gray-200">{fmt(entry.fromDateTime)}</span>
                      </span>
                      {showToDateTime && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> To:&nbsp;
                          <span className="text-gray-200">{fmt(entry.toDateTime)}</span>
                        </span>
                      )}
                    </div>
                    {entry.plan && (
                      <p className="text-gray-300 text-sm mt-2 whitespace-pre-wrap">{entry.plan}</p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      onClick={() => setCurrent({
                        id: entry.id,
                        title: entry.title || '',
                        fromDateTime: entry.fromDateTime || '',
                        toDateTime: entry.toDateTime || '',
                        plan: entry.plan || '',
                        phase: entry.phase || '',
                      })}
                      size="sm"
                      variant="outline"
                      className="border-gray-600 text-gray-300 hover:text-orange-400 min-h-[36px]"
                      data-testid={`pe-${formType}-edit-${entry.id}`}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      onClick={() => remove(entry)}
                      size="sm"
                      variant="outline"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 min-h-[36px]"
                      data-testid={`pe-${formType}-delete-${entry.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PlanEntriesCard;
